import React, { useState, useEffect, useRef } from 'react';
import { Check, ChevronDown, CircleUser } from 'lucide-react';
import { useUpdateCampaignMutation } from '../endpoints/accounts.endpoints';
import { Campaign } from '../types/client-management.types';
import { sortAsc } from '../utils';
import { User } from 'src/services/user/user.types';

interface UserSelectorProps {
  users: Array<User>;
  campaign: Campaign;
  onUserSelect?: (userId: string) => void;
  className?: string;
//   isLoading?: boolean;
}

const UserSelector: React.FC<UserSelectorProps> = ({
  campaign,
  users,
  onUserSelect,
  className,
//   isLoading = false,
}) => {
  const [selectedUser, setSelectedUser] = useState<string>(campaign.owner || '');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filteredUsers, setFilteredUsers] = useState<User[]>(users);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [updateCampaign, { isLoading }] = useUpdateCampaignMutation();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDropdown]);


  const handleUserSelect = (selectedUserId: string) => {
    setSelectedUser(selectedUserId);
    setShowDropdown(false);
    if (onUserSelect) {
      onUserSelect(selectedUserId);
    }
  };

  const handleDropdownToggle = () => {
    setShowDropdown(!showDropdown);
  };

  const handleSearch = (searchQuery: string) => {
    setSearchQuery(searchQuery);
    setFilteredUsers(users.filter(user => user.fullname && user.fullname.toLowerCase().includes(searchQuery && searchQuery.toLowerCase()) || user.email.toLowerCase().includes(searchQuery && searchQuery.toLowerCase())));
  };

  const getOwnerEmail = (userId: string) => {
    return users.find(user => user.id === userId)?.email;
  };

  return (
    <div className="relative max-w-full">
      <div
        onClick={!isLoading ? handleDropdownToggle : undefined}
        className={`text-xs text-gray-500 flex flex-row mt-2 w-fit max-w-full px-2 py-1 rounded-full items-center ${
          isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 cursor-pointer'
        }`}
      >
        <CircleUser className={`size-4 ${isLoading ? '' : 'cursor-pointer'}`} />
        <div
          className={`text-sm font-normal text-gray-800 ml-2 items-center truncate max-w-64 w-fit ${
            isLoading ? '' : 'cursor-pointer'
          }`}
          title={campaign.owner ? getOwnerEmail(campaign.owner) : 'Unassigned'}
        >
          {isLoading ? 'Updating...' : campaign.owner ? getOwnerEmail(campaign.owner) : 'Unassigned'}
        </div>
        <ChevronDown className={`size-4 ml-2 ${isLoading ? '' : 'cursor-pointer'}`} />
      </div>
      {/* {!campaign.user && suggestedUsers && suggestedUsers.length > 0 && (
        <>
          <div
            onClick={() => handleUserSelect(suggestedUsers[0].ext_user_id)}
            className='flex w-full text-sm text-gray-500 ml-2 cursor-pointer underline items-center'
          >
            <Lightbulb className='size-3 mr-1 bg-yellow-500 rounded-full p-0.5 text-white' />
            {suggestedSkus[0].name}
          </div>
        </>
      )} */}

      {campaign.attention_needed && campaign.owner && (
        <div
          onClick={() => handleUserSelect(campaign.owner || '')}
          className='flex w-full text-sm text-gray-500 ml-2 cursor-pointer underline items-center'
        >
          <Check className='size-3 mr-1 bg-green-501 rounded-full p-0.5 text-white' />
          Verify
        </div>
      )}
      {/* Dropdown menu */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto w-fit"
        >
          <div className="p-1">
            <div className="px-3 py-2 text-xs text-gray-500 font-medium border-b border-gray-100">
              <input
                type="text"
                placeholder="Search Users"
                className="w-full text-sm text-gray-500 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
            {users.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">
                No users available
              </div>
            ) : (
              sortAsc(filteredUsers, 'fullname').map((user) => (
                <button
                  key={user.ext_user_id}
                  onClick={() => handleUserSelect(user.id || '')}
                  className={`w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm transition-colors duration-150 flex items-center gap-2 ${
                    selectedUser === user.ext_user_id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  }`}
                >
                  <CircleUser className="size-3 flex-shrink-0" />
                  <span className="truncate">
                    {user.email}
                  </span>
                  {selectedUser === user.ext_user_id && (
                    <div className="ml-auto">
                      <div className="w-2 h-2 bg-blue-500 rounded-full" />
                    </div>
                  )}
                </button>
              ))
            )}
            {campaign.owner && campaign.owner && (
              <>
                <div className="border-t border-gray-100 mt-1 pt-1">
                  <button
                    onClick={() => handleUserSelect('')}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded text-sm transition-colors duration-150 text-gray-500 flex items-center gap-2"
                  >
                    <div className="size-3 flex-shrink-0 border border-gray-300 rounded" />
                    <span>
                      Unassign User
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserSelector;
